const round1 = (value: number): string => (Math.round(value * 10) / 10).toFixed(1);

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${round1(bytes / 1024)} KB`;
  }
  return `${round1(bytes / (1024 * 1024))} MB`;
};

const compactUnits = [
  { limit: 1_000_000_000, suffix: "B" },
  { limit: 1_000_000, suffix: "M" },
  { limit: 1_000, suffix: "K" },
];

export const formatCompactNumber = (value: number): string => {
  const rounded = Math.round(value);
  const magnitude = Math.abs(rounded);
  const sign = rounded < 0 ? "-" : "";
  for (const unit of compactUnits) {
    if (magnitude >= unit.limit) {
      const scaled = Math.round((magnitude / unit.limit) * 10) / 10;
      return `${sign}${scaled}${unit.suffix}`;
    }
  }
  return rounded.toLocaleString();
};

const minuteMs = 60_000;
const hourMs = 60 * minuteMs;
const dayMs = 24 * hourMs;
const weekMs = 7 * dayMs;

export type RelativeTimeOptions = {
  // "3 min ago" and "3 hr ago" instead of "3 minutes ago" and "3 hours ago".
  short?: boolean;
};

const SHORT_UNITS: Record<string, string> = { minute: "min", hour: "hr" };

const ago = (value: number, unit: string, short = false): string => {
  const shortUnit = short ? SHORT_UNITS[unit] : undefined;
  return shortUnit ? `${value} ${shortUnit} ago` : `${value} ${unit}${value === 1 ? "" : "s"} ago`;
};

export const formatRelativeTime = (timestamp: number, now: number = Date.now(), { short = false }: RelativeTimeOptions = {}): string => {
  const elapsed = now - timestamp;
  if (elapsed < minuteMs) {
    return "Just now";
  }
  if (elapsed < hourMs) {
    return ago(Math.floor(elapsed / minuteMs), "minute", short);
  }
  if (elapsed < dayMs) {
    return ago(Math.floor(elapsed / hourMs), "hour", short);
  }
  if (elapsed < 2 * dayMs) {
    return "Yesterday";
  }
  if (elapsed < weekMs) {
    return ago(Math.floor(elapsed / dayMs), "day", short);
  }
  const date = new Date(timestamp);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString(undefined, sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
};

export const formatRelativeTimeShort = (timestamp: number, now: number = Date.now()): string => {
  const elapsed = now - timestamp;
  if (elapsed < minuteMs) {
    return "now";
  }
  if (elapsed < hourMs) {
    return `${Math.floor(elapsed / minuteMs)}m ago`;
  }
  if (elapsed < dayMs) {
    return `${Math.floor(elapsed / hourMs)}h ago`;
  }
  if (elapsed < weekMs) {
    return `${Math.floor(elapsed / dayMs)}d ago`;
  }
  return formatRelativeTime(timestamp, now);
};
